import { useState, type ReactNode, type ComponentType } from 'react';
import { ChevronDown, ClipboardList } from 'lucide-react';

/**
 * Canonical Section-2 content card (rule-16 3-section response skeleton):
 * bordered rounded card, header row = icon + bold title + muted count +
 * collapse chevron. Extracted from ClinicalFactsPanel (ticket #101/WS-5) so
 * every response family shares ONE header anatomy instead of the prior
 * divergent teal+chevron (ClinicalFactsPanel) vs gray no-chevron
 * (ClinicalSummaryCard) shapes. Content-variant rendering (table/list/
 * nested_list/paragraph/status/narrative) stays owned by each caller; this
 * component owns only the shell (border, header, collapse behavior).
 */
export function S2Card({
  title,
  count,
  unit,
  icon: Icon = ClipboardList,
  defaultExpanded = false,
  headerAction,
  children,
}: {
  title: string;
  count: number;
  unit: string;
  icon?: ComponentType<{ className?: string }>;
  defaultExpanded?: boolean;
  /**
   * An action belonging to the whole card — today, the Trend Analysis card's
   * link to the chart page.
   *
   * It renders as a SIBLING of the collapse control, never inside it: an
   * interactive element nested in a button is invalid HTML, and a click on it
   * would also toggle the card. Sitting in the header is what makes it reachable
   * while the card is collapsed, which these cards always are on arrival (#115).
   */
  headerAction?: ReactNode;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div
      data-testid="s2-card"
      className="rounded border border-brand-border bg-linear-to-br from-brand-subtle via-surface-base to-brand-subtle/40 overflow-hidden"
    >
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-left hover:bg-brand-subtle/50 transition-colors"
        >
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-brand-subtle ring-1 ring-inset ring-brand-border">
            <Icon className="w-3.5 h-3.5 text-brand-text" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-foreground">{title}</span>
            <span className="ml-2 text-xs text-text-tertiary">
              {count} {unit}
              {count !== 1 ? 's' : ''}
            </span>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-text-tertiary transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
        {headerAction ? <div className="shrink-0 pl-1 pr-3">{headerAction}</div> : null}
      </div>
      {expanded && (
        <div className="px-4 pb-3.5 space-y-4 border-t border-brand-border/60 pt-3">{children}</div>
      )}
    </div>
  );
}
